import { useEffect, useRef, useState } from "react";
import { X, Camera, Loader2 } from "lucide-react";
import { api, picUrl } from "@/lib/api";
import { toast } from "sonner";

export default function EditProfileDialog({ open, onClose, profile, onSaved }) {
  const fileRef = useRef(null);
  const [name, setName] = useState(profile?.name || "");
  const [bio, setBio] = useState(profile?.bio || "");
  const [whatsapp, setWhatsapp] = useState(profile?.whatsapp_number || "");
  const [waAlerts, setWaAlerts] = useState(profile?.whatsapp_boost_alerts !== false);
  const [preview, setPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(profile?.name || "");
      setBio(profile?.bio || "");
      setWhatsapp(profile?.whatsapp_number || "");
      setWaAlerts(profile?.whatsapp_boost_alerts !== false);
      setPreview(null);
      setAvatarFile(null);
    }
  }, [open, profile]);

  if (!open) return null;

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Image files only");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Max 5 MB");
      return;
    }
    setAvatarFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const sendTest = async () => {
    if (!whatsapp.trim()) {
      toast.error("Enter your WhatsApp number first");
      return;
    }
    setTesting(true);
    try {
      await api.patch("/users/me", {
        whatsapp_number: whatsapp.trim(),
        whatsapp_boost_alerts: waAlerts,
      });
      const r = await api.post("/me/whatsapp-test");
      const d = r.data || {};
      toast.success(`Test sent to ${d.to} · status: ${d.status || "queued"}`, {
        description: d.note,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send test WhatsApp");
    } finally {
      setTesting(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name can't be empty");
      return;
    }
    setSaving(true);
    try {
      let next = profile;
      if (avatarFile) {
        const fd = new FormData();
        fd.append("file", avatarFile);
        const r = await api.post("/users/me/avatar", fd, {
          
        });
        next = r.data;
      }
      if (name !== profile.name || bio !== (profile.bio || "")) {
        const r = await api.patch("/users/me", { name, bio });
        next = r.data;
      }
      const waChanged =
        whatsapp.trim() !== (profile.whatsapp_number || "") ||
        waAlerts !== (profile.whatsapp_boost_alerts !== false);
      if (waChanged) {
        const r = await api.patch("/users/me", {
          whatsapp_number: whatsapp.trim(),
          whatsapp_boost_alerts: waAlerts,
        });
        next = r.data;
      }
      toast.success("Profile updated");
      onSaved?.(next);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const currentAvatar = preview || picUrl(profile?.picture);

  return (
    <div
      data-testid="edit-profile-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      <button
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close"
        data-testid="edit-profile-backdrop"
      />
      <form
        onSubmit={save}
        className="relative w-full max-w-md bg-[#0A0A0C] border border-white/10 shadow-[0_0_40px_rgba(57,255,20,0.15)]"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">
              // edit.identity
            </p>
            <h3 className="font-display font-bold text-lg mt-1">Edit profile</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-profile-close-btn"
            className="h-9 w-9 border border-white/10 hover:border-[#FF007F]/50 hover:text-[#FF007F] flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* avatar */}
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="edit-avatar-btn"
              className="relative h-24 w-24 group overflow-hidden border-2 border-[#39FF14]/50 hover:border-[#39FF14] transition-colors"
            >
              {currentAvatar ? (
                <img
                  src={currentAvatar}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 bg-[#FF007F]/20 flex items-center justify-center font-display font-black text-3xl text-[#FF007F]">
                  {name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Camera className="h-6 w-6 text-[#39FF14]" />
              </div>
            </button>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-white/70">
                profile picture
              </p>
              <p className="text-xs text-white/40 mt-1">
                tap the avatar to change · max 5 MB
              </p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={pickFile}
              data-testid="edit-avatar-input"
            />
          </div>

          {/* name */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block">
              display name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              data-testid="edit-name-input"
              className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-3 py-2.5 focus:outline-none focus:border-[#00FFFF] focus:ring-1 focus:ring-[#00FFFF]/40 font-mono text-sm"
            />
          </div>

          {/* bio */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block">
              bio
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={280}
              rows={4}
              placeholder="Tell the void about yourself…"
              data-testid="edit-bio-input"
              className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-3 py-2.5 focus:outline-none focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F]/40 text-sm resize-none"
            />
            <p className="font-mono text-[10px] text-white/40 mt-1 text-right">
              {bio.length}/280
            </p>
          </div>

          {/* WhatsApp boost alerts */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block">
              whatsapp number · boost alerts
            </label>
            <input
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              inputMode="tel"
              placeholder="+592 659 8080"
              data-testid="edit-whatsapp-input"
              className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-3 py-2.5 focus:outline-none focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366]/40 font-mono text-sm"
            />
            <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={waAlerts}
                onChange={(e) => setWaAlerts(e.target.checked)}
                data-testid="edit-whatsapp-alerts-toggle"
                className="h-4 w-4 accent-[#25D366]"
              />
              <span className="text-xs text-white/70">
                WhatsApp me when someone boosts my video
              </span>
            </label>
            <p className="font-mono text-[10px] text-white/40 mt-1">
              include your country code · leave blank to turn off
            </p>
            <button
              type="button"
              onClick={sendTest}
              disabled={testing || !whatsapp.trim()}
              data-testid="send-test-whatsapp-btn"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-[#25D366]/50 text-[#25D366] text-xs font-mono uppercase tracking-wider hover:bg-[#25D366]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Send test WhatsApp
            </button>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-white/10 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-profile-cancel-btn"
            className="text-white/60 hover:text-white font-mono text-xs uppercase tracking-widest px-4 py-2.5"
          >
            cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="edit-profile-save-btn"
            className="bg-[#39FF14] text-black font-bold uppercase tracking-widest text-xs px-6 py-2.5 hover:shadow-[0_0_20px_rgba(57,255,20,0.5)] disabled:opacity-40 transition-all flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> saving…
              </>
            ) : (
              "save"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
